import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretRight, WifiSlash } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  countedTotalMinor, expectedTotalMinor, hasBlockedPrereqs, nextStep, prevStep, STEP_ORDER,
} from "./utils";
import type {
  DayCloseLabels, DayCloseSnapshot, DayCloseWizardStep,
  DenominationCountEntry, ManagerOverrideInput, NonCashTenderCount,
} from "./types";
import { DayCloseStepIndicator } from "./DayCloseStepIndicator";
import { VarianceSummaryBar } from "./VarianceSummaryBar";
import { PrerequisitesChecklist } from "./PrerequisitesChecklist";
import { CashDenominationGrid } from "./CashDenominationGrid";
import { NonCashTenderPanel } from "./NonCashTenderPanel";
import { VarianceReviewTable } from "./VarianceReviewTable";
import { ManagerOverrideDialog } from "./ManagerOverrideDialog";
import { ZReportPreview } from "./ZReportPreview";
import { DayCloseSuccessState, DayCloseErrorState, DayCloseMobileBlocked } from "./DayCloseStates";

export interface DayCloseWizardProps {
  snapshot: DayCloseSnapshot;
  labels: DayCloseLabels;
  locale: "tr" | "en";
  isBusy?: boolean;
  isOffline?: boolean;
  isMobileBlocked?: boolean;
  onAdvance?: (from: DayCloseWizardStep, to: DayCloseWizardStep) => void;
  onBack?: (from: DayCloseWizardStep, to: DayCloseWizardStep) => void;
  onPatchCashCounts?: (counts: readonly DenominationCountEntry[]) => void;
  onPatchNonCashCounts?: (tenders: readonly NonCashTenderCount[]) => void;
  onPatchDeposit?: (closingFloatMinor: number, depositAmountMinor: number) => void;
  onSubmit?: (override: ManagerOverrideInput | null) => Promise<void> | void;
  onOpenPrerequisite?: (key: string, route: string | null) => void;
  onBackToCashier?: () => void;
  onReopen?: () => void;
  isOwner?: boolean;
  errorMessage?: string | null;
  onRetry?: () => void;
  dataTestId?: string;
}

export function DayCloseWizard({
  snapshot, labels, locale, isBusy = false, isOffline = false, isMobileBlocked = false,
  onAdvance, onBack, onPatchCashCounts, onPatchNonCashCounts, onPatchDeposit, onSubmit,
  onOpenPrerequisite, onBackToCashier, onReopen, isOwner = false,
  errorMessage = null, onRetry, dataTestId,
}: DayCloseWizardProps) {
  const [step, setStep] = React.useState<DayCloseWizardStep>(snapshot.currentStep);
  const [overrideOpen, setOverrideOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  React.useEffect(() => setStep(snapshot.currentStep), [snapshot.currentStep]);

  const closed = snapshot.state === "closed" && snapshot.zReportPayload;

  if (isMobileBlocked) {
    return (
      <div data-testid={dataTestId} className="h-full flex items-center justify-center bg-biz-bg">
        <DayCloseMobileBlocked labels={labels} />
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div data-testid={dataTestId} className="h-full flex items-center justify-center bg-biz-bg">
        <DayCloseErrorState message={errorMessage} labels={labels} onRetry={onRetry} />
      </div>
    );
  }

  if (closed && snapshot.zReportPayload) {
    return (
      <div data-testid={dataTestId} className="h-full overflow-y-auto bg-biz-bg p-6 biz-scrollbar">
        <DayCloseSuccessState
          labels={labels}
          onBackToCashier={onBackToCashier}
          onReopen={onReopen}
          showReopen={isOwner}
        >
          <div className="pt-4">
            <ZReportPreview
              payload={snapshot.zReportPayload}
              labels={labels}
              locale={locale}
              onPrint={() => window.print()}
              onDownload={() => { /* host wires */ }}
            />
          </div>
        </DayCloseSuccessState>
      </div>
    );
  }

  const expected = expectedTotalMinor(snapshot);
  const counted = countedTotalMinor(snapshot);
  const variance = counted - expected;
  const status: "ok" | "warning" | "blocked" =
    snapshot.variance.requiresOwnerOverride
      ? "blocked"
      : snapshot.variance.withinTolerance
      ? "ok"
      : "warning";

  const blockedAdvance =
    isOffline ||
    (step === "prerequisites" && hasBlockedPrereqs(snapshot)) ||
    (step === "submit" && (snapshot.variance.requiresManagerOverride || snapshot.variance.requiresOwnerOverride) && !snapshot.override.present);

  const stepIdx = STEP_ORDER.indexOf(step);
  const isFirst = stepIdx === 0;
  const isFinalAction = step === "submit";

  const handleNext = () => {
    if (blockedAdvance) return;
    if (isFinalAction) {
      setConfirmOpen(true);
      return;
    }
    const to = nextStep(step);
    onAdvance?.(step, to);
    setStep(to);
  };

  const handleBack = () => {
    if (isFirst) return;
    const to = prevStep(step);
    onBack?.(step, to);
    setStep(to);
  };

  const handleConfirmSubmit = async () => {
    setConfirmOpen(false);
    const needsOverride =
      snapshot.variance.requiresManagerOverride || snapshot.variance.requiresOwnerOverride;
    if (needsOverride) {
      setOverrideOpen(true);
      return;
    }
    await onSubmit?.(null);
  };

  return (
    <div data-testid={dataTestId} className="h-full flex flex-col bg-biz-bg">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-biz-border bg-biz-surface-elevated">
        <div className="flex items-center gap-2 text-sm text-biz-foreground-muted">
          <button
            type="button"
            onClick={onBackToCashier}
            className="font-semibold hover:text-biz-foreground transition-colors biz-focus-ring rounded"
          >
            {labels.page.breadcrumb}
          </button>
          <CaretRight className="w-3 h-3" aria-hidden />
          <span className="text-biz-foreground font-semibold">
            {snapshot.branchName} · {new Date(snapshot.businessDate).toLocaleDateString(locale === "tr" ? "tr-TR" : "en-US", { day: "numeric", month: "long", year: "numeric", weekday: "short" })}
          </span>
        </div>
        <div className="text-xs text-biz-foreground-muted hidden md:flex items-center gap-3">
          <span>
            <span className="text-biz-foreground-subtle">{labels.page.openedByLabel}:</span>{" "}
            <span className="font-semibold text-biz-foreground">{snapshot.openedByName}</span>
          </span>
        </div>
      </div>

      <DayCloseStepIndicator
        currentStep={step}
        labels={labels}
        onStepClick={(s) => {
          // allow back-only navigation
          if (STEP_ORDER.indexOf(s) <= stepIdx) setStep(s);
        }}
      />

      {step !== "prerequisites" && (
        <VarianceSummaryBar
          expectedMinor={expected}
          countedMinor={counted}
          varianceMinor={variance}
          status={status}
          labels={labels}
          locale={locale}
        />
      )}

      {isOffline && (
        <div className="flex items-center gap-2 px-6 py-2 bg-biz-warning-subtle text-biz-warning border-b border-biz-warning-border text-sm">
          <WifiSlash className="w-4 h-4" />
          {labels.errors.networkOffline}
        </div>
      )}

      <main className="flex-1 min-h-0 overflow-y-auto biz-scrollbar">
        <div className="max-w-5xl mx-auto p-6">
          <header className="mb-5">
            <h2 className="text-xl font-bold font-display text-biz-foreground">
              {labels.steps[step].title}
            </h2>
            <p className="text-sm text-biz-foreground-muted mt-1">
              {labels.steps[step].description}
            </p>
          </header>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
            >
              {step === "prerequisites" && (
                <PrerequisitesChecklist
                  prerequisites={snapshot.prerequisites}
                  labels={labels}
                  onOpenPrerequisite={onOpenPrerequisite}
                />
              )}
              {step === "cash" && (
                <CashDenominationGrid
                  counts={snapshot.cash.denominationCounts}
                  expectedMinor={snapshot.cash.expectedCashMinor}
                  openingFloatMinor={snapshot.cash.openingFloatMinor}
                  onChange={(c) => onPatchCashCounts?.(c)}
                  labels={labels}
                  locale={locale}
                />
              )}
              {step === "non_cash" && (
                <NonCashTenderPanel
                  tenders={snapshot.nonCashTenders}
                  perTenderToleranceMinor={snapshot.tolerance.perTenderMinor}
                  onChange={(t) => onPatchNonCashCounts?.(t)}
                  labels={labels}
                  locale={locale}
                />
              )}
              {step === "review" && (
                <VarianceReviewTable
                  snapshot={snapshot}
                  onDepositChange={(c, d) => onPatchDeposit?.(c, d)}
                  labels={labels}
                  locale={locale}
                />
              )}
              {step === "submit" && (
                <SubmitPreview
                  snapshot={snapshot}
                  labels={labels}
                  locale={locale}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Footer CTAs */}
      <footer className="flex items-center justify-between gap-3 px-6 py-4 border-t border-biz-border bg-biz-surface-elevated">
        <button
          type="button"
          onClick={handleBack}
          disabled={isFirst}
          className={cn(
            "h-14 min-w-[120px] px-4 rounded-biz-btn font-semibold text-sm biz-focus-ring transition-colors",
            "border border-biz-border-strong text-biz-foreground bg-biz-surface-elevated hover:bg-biz-muted",
            "disabled:opacity-30 disabled:cursor-not-allowed",
          )}
        >
          {labels.cta.back}
        </button>

        <div className="text-xs text-biz-foreground-muted hidden md:block">
          {step === "submit" && (snapshot.variance.requiresManagerOverride || snapshot.variance.requiresOwnerOverride) && (
            <span className="text-biz-warning font-semibold">
              {snapshot.variance.requiresOwnerOverride
                ? labels.errors.ownerOverrideRequired
                : labels.errors.managerOverrideRequired}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleNext}
          disabled={blockedAdvance || isBusy}
          title={blockedAdvance ? (isOffline ? labels.errors.networkOffline : labels.steps.prerequisites.blocked) : undefined}
          className={cn(
            "h-14 min-w-[160px] px-5 rounded-biz-btn font-bold text-sm biz-focus-ring transition-colors",
            isFinalAction
              ? "bg-biz-success text-biz-success-foreground hover:bg-biz-success/90 biz-shadow-md"
              : "bg-biz-primary text-biz-primary-foreground hover:bg-biz-primary-hover biz-shadow-md",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {isFinalAction ? labels.steps.submit.submitCta : labels.cta.next}
        </button>
      </footer>

      <ManagerOverrideDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        labels={labels}
        isBusy={isBusy}
        onSubmit={async (input) => {
          await onSubmit?.(input);
          setOverrideOpen(false);
        }}
      />

      {/* Two-step confirmation */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={labels.steps.submit.title}
        description={labels.steps.submit.description}
        confirmLabel={labels.steps.submit.submitCta}
        cancelLabel={labels.cta.cancel}
        onConfirm={handleConfirmSubmit}
      />
    </div>
  );
}

function SubmitPreview({
  snapshot, labels, locale,
}: { snapshot: DayCloseSnapshot; labels: DayCloseLabels; locale: "tr" | "en" }) {
  const expected = expectedTotalMinor(snapshot);
  const counted = countedTotalMinor(snapshot);
  return (
    <div className="space-y-4">
      <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated p-4 grid gap-3 md:grid-cols-3">
        <Stat label={labels.summary.expected} value={expected} prefix={labels.currencyPrefix} locale={locale} />
        <Stat label={labels.summary.counted} value={counted} prefix={labels.currencyPrefix} locale={locale} bold />
        <Stat label={labels.summary.variance} value={counted - expected} prefix={labels.currencyPrefix} locale={locale} signed />
      </div>
      {snapshot.zReportPayload && (
        <ZReportPreview payload={snapshot.zReportPayload} labels={labels} locale={locale} />
      )}
    </div>
  );
}

function Stat({
  label, value, prefix, locale, bold, signed,
}: { label: string; value: number; prefix: string; locale: "tr" | "en"; bold?: boolean; signed?: boolean }) {
  const body = signed
    ? (value === 0 ? `±${prefix}0` : `${value > 0 ? "+" : "−"}${prefix}${(Math.abs(value) / 100).toLocaleString(locale === "tr" ? "tr-TR" : "en-US", { minimumFractionDigits: 2 })}`)
    : `${prefix}${(value / 100).toLocaleString(locale === "tr" ? "tr-TR" : "en-US", { minimumFractionDigits: 2 })}`;
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted">{label}</div>
      <div className={cn("font-mono-num tabular-nums mt-1", bold ? "text-2xl font-bold" : "text-xl")}>{body}</div>
    </div>
  );
}

function ConfirmDialog({
  open, onOpenChange, title, description, confirmLabel, cancelLabel, onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" role="dialog" aria-modal>
      <div className="bg-biz-surface-elevated rounded-[14px] biz-shadow-lg max-w-sm w-full mx-4 p-5 space-y-4">
        <div>
          <h3 className="text-base font-bold font-display text-biz-foreground">{title}</h3>
          <p className="text-sm text-biz-foreground-muted mt-1">{description}</p>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-12 px-4 rounded-biz-btn border border-biz-border-strong text-biz-foreground font-semibold biz-focus-ring"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="h-12 px-5 rounded-biz-btn bg-biz-success text-biz-success-foreground font-bold biz-shadow-md hover:bg-biz-success/90 biz-focus-ring"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
